import type { ViewEndpoint } from "../protocol/events";

/** Staging-only operational counts/errors. Never send images, card names or the scan log. */
export function mountStagingDiagnostics(endpoint: ViewEndpoint, video: HTMLVideoElement): void {
  if (location.hostname !== "webscan-staging.fdrechsler.com") return;
  const state = {
    id: crypto.randomUUID(),
    build: import.meta.env.VITE_BUILD_ID as string,
    ua: navigator.userAgent.slice(0, 256),
    stage: "boot",
    backend: "pending",
    width: 0,
    height: 0,
    frames: 0,
    quads: 0,
    reads: 0,
    finds: 0,
    lastFrameMs: 0,
    timingSamples: 0,
    detectorTotalMs: 0,
    inferTotalMs: 0,
    grabTotalMs: 0,
    queueTotalMs: 0,
    readTotalMs: 0,
    readMaxMs: 0,
    prepareTotalMs: 0,
    latencyTotalMs: 0,
    rejected: 0,
    targetFps: 0,
    ceilingFps: 0,
    cardHeight: 0,
    errors: [] as string[],
  };
  let sent = 0;
  let lastSent = -1000;
  const send = (): void => {
    if (sent >= 30 || performance.now() - lastSent < 1000) return;
    sent++;
    lastSent = performance.now();
    void fetch("/__diagnostics", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        ...state,
        uptime: Math.round(performance.now()),
        cameraTimeMs: Math.round(video.currentTime * 1000),
        decodedFrames: video.getVideoPlaybackQuality?.().totalVideoFrames ?? 0,
        videoReadyState: video.readyState,
        videoPaused: video.paused,
      }),
      keepalive: true,
    }).catch(() => {});
    if (sent === 30) clearInterval(timer);
  };
  const failure = (message: string): void => {
    state.errors = [...state.errors, message.slice(0, 200)].slice(-4);
    send();
  };
  endpoint.on("boot:task", ({ label }) => {
    state.stage = label.slice(0, 48);
  });
  endpoint.on("boot:failed", ({ message }) => failure(message));
  endpoint.on("boot:error", ({ message }) => {
    state.stage = "boot-error";
    failure(message);
  });
  endpoint.on("boot:ready", ({ backend }) => {
    state.backend = backend;
    state.stage = "ready";
    send();
  });
  endpoint.on("camera:opened", ({ sensorWidth, sensorHeight }) => {
    state.width = sensorWidth;
    state.height = sensorHeight;
  });
  endpoint.on("camera:failed", ({ message }) => failure(message));
  endpoint.on("session:started", () => {
    state.stage = "scanning";
  });
  endpoint.on("session:stopped", () => {
    state.stage = "stopped";
    send();
  });
  endpoint.on("frame", ({ at, quads }) => {
    state.frames++;
    state.lastFrameMs = Math.round(at);
    if (quads.length) state.quads++;
  });
  endpoint.on("detector:timing", (timing) => {
    state.timingSamples++;
    state.detectorTotalMs += Math.round(timing.frameMs);
    state.inferTotalMs += Math.round(timing.inferMs);
    state.grabTotalMs += Math.round(timing.grabMs);
    state.queueTotalMs += Math.round(timing.queueMs);
    state.targetFps = timing.targetFps;
    state.ceilingFps = timing.ceilingFps;
  });
  endpoint.on("read:sampled", ({ cardHeight }) => {
    state.cardHeight = Math.round(cardHeight);
  });
  endpoint.on("read:done", ({ ms, verdict, prepareMs, latencyMs }) => {
    state.reads++;
    state.readTotalMs += Math.round(ms);
    state.readMaxMs = Math.max(state.readMaxMs, Math.round(ms));
    state.prepareTotalMs += Math.round(prepareMs);
    state.latencyTotalMs += Math.round(latencyMs);
    if (verdict === "REJECTED") state.rejected++;
  });
  endpoint.on("found", () => {
    state.finds++;
  });
  endpoint.on("status", ({ kind, text }) => {
    if (kind === "error" || kind === "crashed") failure(text);
  });
  addEventListener("error", (event) => failure(event.message));
  addEventListener("unhandledrejection", (event) => failure(String(event.reason)));
  addEventListener("pagehide", send);
  const timer = setInterval(send, 10000);
  send();
}
